export type MobileDrawerLayer =
  | 'cart'
  | 'date-request'
  | 'date-request-matching-dates'
  | 'active-offers'
  | 'wishlist'
  | 'waitlist'

interface DrawerHistoryState {
  kftDrawer?: MobileDrawerLayer
}

type LayerHandler = () => void
type LayerIsOpen = () => boolean

const SIMPLE_DRAWER_LAYERS = [
  'cart',
  'active-offers',
  'wishlist',
  'waitlist',
] as const satisfies readonly MobileDrawerLayer[]

const layerStack: MobileDrawerLayer[] = []
const closeHandlers = new Map<MobileDrawerLayer, LayerHandler>()
const openHandlers = new Map<MobileDrawerLayer, LayerHandler>()
const isOpenHandlers = new Map<MobileDrawerLayer, LayerIsOpen>()

let ignoreNextPopstate = false
let listenerRegistered = false

export const isMobileDrawerBackEnabled = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    return useDevice().isMobile
  } catch {
    return false
  }
}

export const getDrawerLayerFromHistory = (): MobileDrawerLayer | null => {
  if (typeof history === 'undefined') return null

  const state = history.state as DrawerHistoryState | null
  return state?.kftDrawer ?? null
}

const rebuildLayerStackFromHistoryLayer = (
  layer: MobileDrawerLayer | null
): MobileDrawerLayer[] => {
  switch (layer) {
    case 'date-request-matching-dates':
      return ['date-request', 'date-request-matching-dates']
    case 'date-request':
    case 'cart':
    case 'active-offers':
    case 'wishlist':
    case 'waitlist':
      return [layer]
    default:
      return []
  }
}

const isLayerOpen = (layer: MobileDrawerLayer) =>
  isOpenHandlers.get(layer)?.() ?? false

const syncDrawersToHistoryState = () => {
  const targetLayer = getDrawerLayerFromHistory()
  layerStack.length = 0
  layerStack.push(...rebuildLayerStackFromHistoryLayer(targetLayer))

  const wantMatching = targetLayer === 'date-request-matching-dates'
  const wantDateRequest = targetLayer === 'date-request' || wantMatching

  if (!wantMatching && isLayerOpen('date-request-matching-dates')) {
    closeHandlers.get('date-request-matching-dates')?.()
  }
  if (!wantDateRequest && isLayerOpen('date-request')) {
    closeHandlers.get('date-request')?.()
  }

  for (const layer of SIMPLE_DRAWER_LAYERS) {
    if (targetLayer !== layer && isLayerOpen(layer)) {
      closeHandlers.get(layer)?.()
    }
  }

  for (const layer of SIMPLE_DRAWER_LAYERS) {
    if (targetLayer === layer && !isLayerOpen(layer)) {
      openHandlers.get(layer)?.()
    }
  }

  if (wantDateRequest && !isLayerOpen('date-request')) {
    openHandlers.get('date-request')?.()
  }
  if (wantMatching && !isLayerOpen('date-request-matching-dates')) {
    openHandlers.get('date-request-matching-dates')?.()
  }
}

const onPopstate = () => {
  if (ignoreNextPopstate) {
    ignoreNextPopstate = false
    return
  }

  if (!isMobileDrawerBackEnabled()) return

  syncDrawersToHistoryState()
}

const ensureListener = () => {
  if (listenerRegistered || typeof window === 'undefined') return
  listenerRegistered = true
  window.addEventListener('popstate', onPopstate)
}

export const registerMobileDrawerCloseHandler = (
  layer: MobileDrawerLayer,
  close: LayerHandler
) => {
  closeHandlers.set(layer, close)
}

export const registerMobileDrawerOpenHandler = (
  layer: MobileDrawerLayer,
  open: LayerHandler
) => {
  openHandlers.set(layer, open)
}

export const registerMobileDrawerIsOpenHandler = (
  layer: MobileDrawerLayer,
  isOpen: LayerIsOpen
) => {
  isOpenHandlers.set(layer, isOpen)
}

export const pushMobileDrawerHistory = (layer: MobileDrawerLayer) => {
  if (!isMobileDrawerBackEnabled()) return

  ensureListener()
  history.pushState({ kftDrawer: layer }, '')
  layerStack.push(layer)
}

export const popMobileDrawerHistory = (layer: MobileDrawerLayer) => {
  if (!isMobileDrawerBackEnabled()) return

  const index = layerStack.lastIndexOf(layer)
  if (index === -1) return

  const entriesToPop = layerStack.length - index
  layerStack.splice(index)

  if (entriesToPop > 0) {
    ignoreNextPopstate = true
    history.go(-entriesToPop)
  }
}

/** Drop tracked history entries without calling history.go (e.g. before route navigation). */
export const discardMobileDrawerHistory = (layer: MobileDrawerLayer) => {
  const index = layerStack.lastIndexOf(layer)
  if (index === -1) return

  layerStack.splice(index)
}

/** @internal Test helper */
export const __resetMobileDrawerHistoryForTests = () => {
  layerStack.length = 0
  closeHandlers.clear()
  openHandlers.clear()
  isOpenHandlers.clear()
  ignoreNextPopstate = false
  listenerRegistered = false
}

/** @internal Test helper */
export const __getMobileDrawerHistoryStackForTests = () => [...layerStack]

/** @internal Test helper */
export const __syncDrawersToHistoryStateForTests = () => {
  syncDrawersToHistoryState()
}
